import type { DayDelta, PlanningDay } from "./planning.api";

// Planning v3: the four steps of the static planning and De dag (docs/design_handoff_planning_v3/README.md §3, §4.0).
// What each step says on the step bar, when it is done, and the chips of what a choice changes. Pure; the words are
// i18n keys with their values.

export const PLAN_STEPS = [1, 2, 3, 4] as const;
export type PlanStep = (typeof PLAN_STEPS)[number];

export function isPlanStep(value: unknown): value is PlanStep {
  return PLAN_STEPS.includes(value as PlanStep);
}

/** A step in the address, as a word: `?view=plan&step=overview`. The first step has none. */
const STEP_WORDS: Record<PlanStep, string> = { 1: "full", 2: "plan", 3: "overview", 4: "capacity" };

export function stepWord(step: PlanStep): string | undefined {
  return step === 1 ? undefined : STEP_WORDS[step];
}

export function stepOfWord(word: unknown): PlanStep {
  return PLAN_STEPS.find((step) => STEP_WORDS[step] === word) ?? 1;
}

/** A text with its values: `t(key, params)`. */
export interface Said {
  key: string;
  params?: Record<string, string | number>;
}

export interface StepState {
  step: PlanStep;
  title: Said;
  count: Said;
  done: boolean;
}

const TITLES: Record<PlanStep, string> = {
  1: "planning.v3.step1",
  2: "planning.v3.step2",
  3: "planning.v3.step3",
  4: "planning.v3.step4",
};

/**
 * What each step says, from the day as it stands: the full deliveries still open or the trips made of them and how many
 * have something behind them; what is open; the overview; how many trips have their lorry. Done is what the day shows:
 * no full delivery open any more, nothing left to plan, every trip with a lorry or charter. The overview is never done
 * by itself: it is for looking.
 */
export function stepStates(day: PlanningDay | undefined): StepState[] {
  const steps = day?.steps;
  const full = steps?.fullDeliveries;
  return PLAN_STEPS.map((step): StepState => {
    const title = { key: TITLES[step] };
    if (!steps || !full) return { step, title, count: { key: "planning.v3.countLoading" }, done: false };
    switch (step) {
      case 1:
        if (full.open > 0 || full.trips === 0) {
          return { step, title, count: { key: "planning.v3.count1Open", params: { count: full.open } }, done: false };
        }
        return { step, title, count: { key: "planning.v3.count1Trips", params: { count: full.trips, next: full.withNext } }, done: true };
      case 2:
        return { step, title, count: { key: "planning.v3.count2", params: { count: steps.open } }, done: steps.open === 0 && steps.trips > 0 };
      case 3:
        return { step, title, count: { key: "planning.v3.count3", params: { trips: steps.trips, open: steps.open } }, done: false };
      case 4:
        return {
          step,
          title,
          count: steps.trips > 0 ? { key: "planning.v3.count4", params: { assigned: steps.assigned, count: steps.trips } } : { key: "planning.v3.count4None" },
          done: steps.trips > 0 && steps.assigned === steps.trips,
        };
    }
  });
}

export type DeltaTone = "neutral" | "warning" | "success";

/** A signed whole number, the way the chips write it: +2, −7, ±0. */
export function signed(value: number, format: (value: number) => string = String): string {
  const rounded = Math.round(value);
  return `${rounded > 0 ? "+" : rounded < 0 ? "−" : "±"}${format(Math.abs(rounded))}`;
}

/**
 * The chips of what a choice changes (§4.0): a pendel more in warning (one less is good), the kilometres, the empty
 * kilometres when they change (fewer is good), and the time.
 */
export function deltaChips(delta: DayDelta): (Said & { tone: DeltaTone })[] {
  const chips: (Said & { tone: DeltaTone })[] = [];
  if (delta.pendels !== 0) {
    const one = delta.addedPendels.length === 1 && delta.pendels === 1 ? delta.addedPendels[0]! : null;
    chips.push(
      one
        ? { key: "planning.v3.deltaPendelPair", params: { from: one.from, to: one.to }, tone: "warning" }
        : { key: "planning.v3.deltaPendels", params: { value: signed(delta.pendels) }, tone: delta.pendels > 0 ? "warning" : "success" },
    );
  }
  if (delta.runs !== 0) chips.push({ key: "planning.v3.deltaRuns", params: { value: signed(delta.runs) }, tone: delta.runs > 0 ? "warning" : "success" });
  chips.push({ key: "planning.v3.deltaKm", params: { value: signed(delta.km) }, tone: delta.km > 0 ? "neutral" : "success" });
  if (Math.round(delta.emptyKm) !== 0) {
    chips.push({ key: "planning.v3.deltaEmpty", params: { value: signed(delta.emptyKm) }, tone: delta.emptyKm < 0 ? "success" : "neutral" });
  }
  chips.push({ key: "planning.v3.deltaTime", params: { minutes: delta.minutes }, tone: "neutral" });
  return chips;
}
